import { Command, CommandResult } from '@/_core/cqrs/types/commands';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { PersonaCustomField, PersonaPersonality, ViewType } from '../../store/types';
import { ActivityModel, DescriptionFormat, SubtaskModel, SubtaskStatusModel, TaskModel, TrashOperationResultCode } from '../queries';
import { BoardVisibility } from '@/api/common';
import type { PublishedStorymapGateSettings } from '@/pages/main/components/storymapSharing/types';

export * from './trashCard';
export * from './integration';
export * from './initializeReleaseViewOrder';
export type { PublishedStorymapGateSettings } from '@/pages/main/components/storymapSharing/types';

export interface PublishedStorymapSharingResult extends CommandResult {
    Visibility: BoardVisibility;
    CanWorkspaceMembersComment: boolean;
    IsPublicSharingEnabled: boolean;
    IsNextGenPrivateLinkSharingEnabled: boolean;
    CanCopyNextGenPrivateLink: boolean;
    AllowEditorsToCopyPrivateLinks: boolean;
    Version: number;
    NextGenPrivateLinkCreatedByUser?: string;
    NextGenPrivateLinkCreatedAt?: Date;
    NextGenPublicGates: PublishedStorymapGateSettings;
    NextGenPrivateGates: PublishedStorymapGateSettings;
    PublicAllowAttachments: boolean;
    NextGenPrivateAllowAttachments: boolean;
}

export interface PrivateLinkCopyResult extends CommandResult {
    PrivateLinkUrl: string;
}

abstract class PublishedStorymapSharingCommand extends Command<PublishedStorymapSharingResult> {
    public BoardActivity: any = null;

    public constructor(commandName: string, public StorymapId: string) {
        super(commandName);
    }
}

export class UpdateStorymapSharingSettings extends PublishedStorymapSharingCommand {
    constructor(
        public StorymapId: string,
        public Visibility: BoardVisibility,
        public CanWorkspaceMembersComment: boolean,
        public IsNextGenPrivateLinkSharingEnabled: boolean,
        public AllowEditorsToCopyNextGenPrivateLinks: boolean,
        public NextGenPublicGates: PublishedStorymapGateSettings,
        public NextGenPrivateGates: PublishedStorymapGateSettings,
        // null = keep the stored allowance (sent only when changed from the loaded baseline).
        public PublicAllowAttachments: boolean | null = null,
        public NextGenPrivateAllowAttachments: boolean | null = null
    ) {
        super('UpdateStorymapSharingSettings', StorymapId);
    }
}

export class RegenerateNextGenPrivateLinkToken extends PublishedStorymapSharingCommand {
    constructor(public StorymapId: string) {
        super('RegenerateNextGenPrivateLinkToken', StorymapId);
    }
}

export class CopyNextGenStorymapPrivateLink extends Command<PrivateLinkCopyResult> {
    public BoardActivity: any = null;

    constructor(public StorymapId: string) {
        super('CopyNextGenStorymapPrivateLink');
    }
}

export enum CardType {
    Unknown,
    Activity,
    Task,
    Subtask,
    UnscheduledSubtask,
}

export class MoveCard extends Command<MoveCardResult> {
    public BoardActivity: any;

    public constructor(
        public StorymapId: string,
        public Id: string,
        public SourceType: CardType,
        public SourceActivityId: string,
        public SourceTaskId: string,
        public SourceReleaseId: string,
        public SourceIndex: number,

        public DestinationType: CardType,
        public DestinationActivityId: string,
        public DestinationTaskId: string,
        public DestinationReleaseId: string,
        public DestinationIndex: number
    ) {
        super('MoveCard');
    }
}

export class MoveCardResult extends CommandResult {
    public PushedActivity: { Id: string; LinkedItemUrl: string };
    public PushedTask: { Id: string; LinkedItemUrl: string };
    public Release: { ReleaseId: string; LinkedItemUrl: string };
    public UnlinkedCardIds: string[];
}

export class ChangeCardName extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public Id: string, public StorymapId: string, public Name: string, public CardType: CardType) {
        super('ChangeCardName');
    }
}

export class AddNewActivity extends Command<AddNewCardResult> {
    public BoardActivity: any;

    public constructor(
        public Id: string,
        public StorymapId: string,
        public Name: string,
        public Description: string,
        public Order: number,
        public Color: number
    ) {
        super('AddNewActivity');
    }
}

export class AddNewSubtask extends Command<AddNewCardResult> {
    public BoardActivity: any;

    public constructor(
        public Id: string,
        public StorymapId: string,
        public Name: string,
        public Description: string,
        public Order: number,
        public TaskId: string,
        public ReleaseId: string,
        public Color: number,
        public Estimation?: number
    ) {
        super('AddNewSubtask');
    }
}

export class AddNewTask extends Command<AddNewCardResult> {
    public BoardActivity: any;

    public constructor(
        public Id: string,
        public StorymapId: string,
        public Name: string,
        public Description: string,
        public Order: number,
        public ActivityId: string,
        public Color: number
    ) {
        super('AddNewTask');
    }
}

export interface AddNewCardResult extends CommandResult {
    CardKey: string;
}

export class RestoreDeletedCard extends Command<RestoreDeletedCardResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public CardId: string, public CardName: string, public CardType: CardType) {
        super('RestoreDeletedCard');
    }
}

export class DeleteActivity extends Command<DeleteCardResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string) {
        super('DeleteActivity');
    }
}

export class DeleteTask extends Command<DeleteCardResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string) {
        super('DeleteTask');
    }
}

export class DeleteSubtask extends Command<DeleteCardResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string) {
        super('DeleteSubtask');
    }
}

export class EmptyTrashBin extends Command<CommandResult> {
    public constructor(public StorymapId: string) {
        super('EmptyTrashBin');
    }
}

export interface RestoreDeletedCardResult extends CommandResult {
    ResultCode: TrashOperationResultCode;
    BoardActivity: any;
    RestoredCardId: string;
    RestoredCardName: string;
    Activities: ActivityModel[];
    NextGenRestoredCards: NextGenRestoredCards;
}

export interface DeleteCardResult extends CommandResult {
    ResultCode: TrashOperationResultCode;
}

export interface NextGenRestoredCards {
    Activities: ActivityModel[];
    Tasks: TaskModel[];
    Subtasks: SubtaskModel[];
}

export class AddOrUpdateStorymapColorMapping extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public CardColor: number, public ColorLabel: string, public IsEnabled: boolean) {
        super('AddOrUpdateStorymapColorMapping');
    }
}

export class MoveReleaseToIndex extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string, public Index: number) {
        super('MoveReleaseToIndex');
    }
}

export class ArchiveRelease extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string) {
        super('ArchiveRelease');
    }
}

export class ReactivateRelease extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string) {
        super('ReactivateRelease');
    }
}

export class RemoveRelease extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string, public MoveSubtasksTo: string) {
        super('RemoveRelease');
    }
}

export class ChangeReleaseName extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string, public Name: string) {
        super('ChangeReleaseName');
    }
}

export class AddNewRelease extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string, public Name: string, public Order: number) {
        super('AddNewRelease');
    }
}

export class PushCardsToIntegrationTool extends Command<PushCardsToIntegrationToolResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public ReleaseId: string) {
        super('PushCardsToIntegrationTool');
    }
}

export class PushCardsToIntegrationToolResult extends CommandResult {
    public NumberOfPushedCards: number;
    public Release: { LinkedItemUrl: string };
}

export class AddPersonasToCard extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public CardId: string, public OrderedPersonaIds: string[]) {
        super('AddPersonasToCard');
    }
}

export class RemovePersonasFromCard extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public CardId: string, public OrderedPersonaIds: string[]) {
        super('RemovePersonasFromCard');
    }
}

export class UpdatePersonaName extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string, public Name: string) {
        super('UpdatePersonaName');
    }
}

export class CreatePersona extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string, public Name: string, public PictureId: string,
        public Group: string = null, public Description: string = null, public CustomFields: PersonaCustomField[] = null,
        public Personalities: PersonaPersonality[] = null
    ) {
        super('CreatePersona');
    }
}

export class UpdatePersonaGroup extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string, public Group: string) {
        super('UpdatePersonaGroup');
    }
}

export class UpdatePersonaDescription extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string, public Description: string) {
        super('UpdatePersonaDescription');
    }
}


export class UpdatePersonaCustomFieldOnStorymap extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string, public CustomFieldId: string, public Value: string) {
        super('UpdatePersonaCustomFieldOnStorymap');
    }
}

export class UpdatePersonaPersonalityFieldOnStorymap extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string, public PersonalityFieldId: string, public Value: number) {
        super('UpdatePersonaPersonalityFieldOnStorymap');
    }
}

export class UpdatePersonaPicture extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string, public PictureId: string) {
        super('UpdatePersonaPicture');
    }
}

export class DeletePersona extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string) {
        super('DeletePersona');
    }
}

export class SaveAboutBoard extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public AboutBordText: string) {
        super('SaveAboutBoard');
    }
}

export class SaveAiDialogData extends Command<SaveAiDialogDataResult> {
    public constructor(
        public StorymapId: string,
        public TargetUsers: string,
        public TypeOfProduct: string,
        public Purpose: string,
        public KeyFeatures: string,
        public PlatformCompatibility: string,
        public PricingModel: string,
        public AdditionalNotes: string,
        public Language: string,
    ) {
        super('SaveAiDialogData');
    }
}

export interface SaveAiDialogDataResult extends CommandResult {
    StatusCode: number;
}

export class AddAnnotation extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string, public Name: string, public Color: number, public Icon: number) {
        super('AddAnnotation');
    }
}

export class DeleteAnnotation extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string, public Name: string) {
        super('DeleteAnnotation');
    }
}

export class EditAnnotation extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string, public Name: string, public Color: number, public Icon: number) {
        super('EditAnnotation');
    }
}

export class MoveAnnotation extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Id: string, public NewOrder: number) {
        super('MoveAnnotation');
    }
}

export class ChangeCardPrio extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(
        public StorymapId: string,
        public CardId: string,

        public PrioValue: number,
        public Framework: number
    ) {
        super('ChangeCardPrio');
    }
}

export class ChangeCardEffort extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(
        public StorymapId: string,
        public CardId: string,

        public Effort: number
    ) {
        super('ChangeCardEffort');
    }
}

export class ChangeCardBusinessValue extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(
        public StorymapId: string,
        public CardId: string,

        public BusinessValue: number
    ) {
        super('ChangeCardBusinessValue');
    }
}

export class ChangeStorymapPrioFramework extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(
        public StorymapId: string,

        public Framework: number
    ) {
        super('ChangeStorymapPrioFramework');
    }
}

export class ChangeCardDescription extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public Id: string, public StorymapId: string, public Description: string, public CardType: string) {
        super('ChangeCardDescription');
    }
}

export class SaveViewOptions extends Command<CommandResult> {
    public constructor(
        public StorymapId: string,
        public ViewType: ViewType,
        public IsPrioEnabled: boolean,
        public IsAnnotationEnabled: boolean,
        public IsPersonaEnabled: boolean,
        public IsIssueTypeEnabled: boolean,
        public IsAssigneeEnabled: boolean
    ) {
        super('SaveViewOptions');
    }
}

export class SaveLastViewType extends Command<CommandResult> {
    public constructor(
        public StorymapId: string,
        public ViewType: ViewType,
    ) {
        super('SaveLastViewType');
    }
}

export class ChangeStorymapName extends Command<CommandResult> {
    public constructor(
        public StorymapId: string,
        public Name: string,
    ) {
        super('ChangeStorymapName');
    }
}

export class EditStoryMapSlug extends Command<EditStoryMapSlugResult> {
    public constructor(
        public StorymapId: string,
        public Slug: string,
    ) {
        super('EditStoryMapSlug');
    }
}

export interface EditStoryMapSlugResult extends CommandResult {
    Slug: string;
}

export class ChangeStoryMapCollection extends Command<CommandResult> {
    public constructor(
        public StorymapId: string,
        public CollectionId: string,
    ) {
        super('ChangeStoryMapCollection');
    }
}

export class ToggleArchivedState extends Command<CommandResult> {
    public constructor(
        public StorymapId: string
    ) {
        super('ToggleArchivedState');
    }
}

export class DeleteStoryMap extends Command<CommandResult> {
    public constructor(
        public StorymapId: string
    ) {
        super('DeleteStoryMap');
    }
}

export class ChangeStorymapEstimationUnit extends Command<CommandResult> {
    public constructor(
        public StorymapId: string,
        public EstimationUnit: string
    ) {
        super('ChangeStorymapEstimationUnit');
    }
}

export class SetUserStoryTemplates extends Command<CommandResult>
{
    public constructor(
        public StorymapId: string,
        public IsStoryTemplateEnabled: boolean,
        public IsActivityTemplateEnabled: boolean,
        public IsTaskTemplateEnabled: boolean,
        public StoryTemplate: string,
        public ActivityTemplate: string,
        public TaskTemplate: string
    ) {
        super('SetUserStoryTemplates');
    }
}

export class AddNewCustomAiStoryTemplate extends Command<CommandResult> {
    public constructor(
        public Id: string,
        public StorymapId: string,
        public Name: string,
        public Body: string,
        public Target: number,
        public IsVisibleByDefault: boolean = false
    ) {
        super('AddNewCustomAiStoryTemplate');
    }
}

export class ChangeStorymapCustomAiStoryTemplate extends Command<CommandResult> {
    public constructor(
        public Id: string,
        public StorymapId: string,
        public Name: string,
        public Body: string,
        public Target: number,
        public IsVisibleByDefault: boolean = false
    ) {
        super('ChangeStorymapCustomAiStoryTemplate');
    }
}

export class ChangeStorymapAiLanguage extends Command<CommandResult> {
    public constructor(
        public StorymapId: string,
        public Language: string,
    ) {
        super('ChangeStorymapAiLanguage');
    }
}

export class DeleteStorymapCustomAiStoryTemplate extends Command<CommandResult> {
    public constructor(
        public Id: string,
        public StorymapId: string
    ) {
        super('DeleteStorymapCustomAiStoryTemplate');
    }
}

export class SetStorymapAiTemplateOverride extends Command<CommandResult> {
    public constructor(
        public StorymapId: string,
        public LibraryTemplateId: string,
        public IsVisible: boolean
    ) {
        super('SetStorymapAiTemplateOverride');
    }
}


export class DismissStorymapOnboarding extends Command<CommandResult> {
    public constructor(public StorymapId: string) {
        super('DismissStorymapOnboarding');
    }
}

export class SetStatusColor extends Command<CommandResult> {
    public constructor(public StorymapId: string, public Key: number, public Color: string) {
        super('SetStatusColor');
    }
}

export class RenameStatus extends Command<CommandResult> {
    public constructor(public StorymapId: string, public Key: number, public Name: string) {
        super('RenameStatus');
    }
}

export class DeleteStatus extends Command<CommandResult> {
    public constructor(
        public StorymapId: string,
        public Key: number,
        public IsInUse: boolean,
        public NewStatusKey: number
    ) {
        super("DeleteStatus");

    }
}
export class AddNewStatus extends Command<AddNewStatusResult> {
    public constructor(
        public StorymapId: string,
        public Name: string,
        public Color: string,
        public Category: number,
        public Order: number
    ) {
        super("AddNewStatus");

    }
}

export interface AddNewStatusResult extends CommandResult {
    Key: number;
}

export class MoveStatus extends Command<CommandResult> {
    public constructor(
        public StorymapId: string,
        public Key: number,
        public Order: number,
    ) {
        super("MoveStatus");

    }
}

export class RestoreDefaultStatuses extends Command<RestoreDefaultStatusesResult> {
    public constructor(public StorymapId: string) {
        super('RestoreDefaultStatuses');
    }
}

export interface RestoreDefaultStatusesResult extends CommandResult {
    RestoredStatuses: SubtaskStatusModel[];
}

export class SetBoardVisibilityParams extends Command<CommandResult> {
    public constructor(
        public StorymapId: string,
        public Visibility: number,
        public CanWorkspaceMembersComment: boolean,
    ) {
        super("SetBoardVisibilityParams");
    }
}

export class SetViewersCanCommentOnCards extends Command<CommandResult> {
    constructor(public StorymapId: string, public ViewersCanCommentOnCards: boolean) {
        super('SetViewersCanCommentOnCards');
    }
}

export class AddWorkspaceMemberToCollaborators extends Command<CommandResult> {

    public constructor(
        public StorymapId: string,
        public Id: string,
        public Role: number
    ) {
        super("AddWorkspaceMemberToCollaborators");
    }
}

export class RemoveCollaborator extends Command<CommandResult> {

    public constructor(
        public StorymapId: string,
        public Email: string
    ) {
        super("RemoveCollaborator");
    }
}

export class ChangeCollaboratorRole extends Command<CommandResult> {

    public constructor(
        public StorymapId: string,
        public Email: string,
        public Role: number
    ) {
        super("ChangeCollaboratorRole");
    }
}

export interface ExcludedImportItem {
    Id: string;
    Title: string;
    WorkItemType: string;
    Reason: string;
    Url: string | null;
}

export interface ImportCardsResult extends CommandResult {
    ExcludedItems?: ExcludedImportItem[];
    TotalExcludedCount?: number;
    FailedToProcessCount?: number;
}

export class ImportCards extends Command<ImportCardsResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string) {
        super('ImportCards');
    }
}

export class ChangeStorymapTheme extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public Theme: number) {
        super('ChangeStorymapTheme');
    }
}

export class ChangeStorymapCardDefaultColors extends Command<CommandResult> {
    public BoardActivity: any;

    public constructor(public StorymapId: string, public ActivityColor: number, public TaskColor: number, public SubtaskColor: number) {
        super('ChangeStorymapCardDefaultColors');
    }
}

export class ReorderCardInStatus extends Command<CommandResult> {
    constructor(public CardId: string, public StorymapId: string, public StatusKey: number, public Index: number) {
        super('ReorderCardInStatus');
    }
}


export class SetCardKeyIsEnabled extends Command<CommandResult> {
    constructor(public StorymapId: string, public IsEnabled: boolean, public CardKeyPrefix: string) {
        super('SetCardKeyIsEnabled');
    }
}


export class ArchiveActivity extends Command<CommandResult> {
    public BoardActivity: any;

    constructor(public StorymapId: string, public CardId: string) {
        super('ArchiveActivity');
    }
}


export class RestoreArchivedActivity extends Command<CommandResult> {
    public BoardActivity: any;

    constructor(public StorymapId: string, public CardId: string) {
        super('RestoreArchivedActivity');
    }
}

export class ImportExcelToStorymap extends Command<ImportExcelToStorymapResult> {
    public BoardActivity: any;

    constructor(public StorymapId: string, public TempFileId: string, public DescriptionFormat: DescriptionFormat) {
        super('ImportExcelToStorymap');
    }
}

export interface ImportExcelToStorymapResult extends CommandResult {
    RowsImported: number;
    RowsSkipped: number;
}
